/**
 * Currency formatting utility for kiosk display
 * Ensures consistent price formatting throughout the app
 */

/**
 * Format a number as USD currency
 * @param {number} amount - The amount to format
 * @param {boolean} showCents - Whether to always show cents (default: true)
 * @returns {string} Formatted currency string (e.g., "$12.99")
 */
export function formatCurrency(amount, showCents = true) {
  if (amount == null || isNaN(amount)) {
    return showCents ? '$0.00' : '$0';
  }

  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(absAmount);

  return amount < 0 ? `-${formatted}` : formatted;
}

/**
 * Format price with optional "+" prefix for add-ons
 * @param {number} amount - The amount to format
 * @param {boolean} showPlus - Whether to show "+" for positive amounts
 * @returns {string} Formatted price (e.g., "+$2.00")
 */
export function formatPriceModifier(amount, showPlus = true) {
  if (amount == null || isNaN(amount) || amount === 0) {
    return '';
  }
  
  const formatted = formatCurrency(amount);
  return showPlus && amount > 0 ? `+${formatted}` : formatted;
}

/**
 * Format price for display in item cards
 * @param {number} basePrice - The base price
 * @param {number} [fromPrice] - Optional "from" price for items with required modifiers
 * @returns {string} Formatted price string
 */
export function formatItemPrice(basePrice, fromPrice = null) {
  if (fromPrice !== null && fromPrice !== basePrice) {
    return `From ${formatCurrency(fromPrice)}`;
  }
  return formatCurrency(basePrice);
}

/**
 * Parse a currency string back to a number
 * @param {string} currencyString - Currency string (e.g., "$12.99")
 * @returns {number} Parsed number
 */
export function parseCurrency(currencyString) {
  if (!currencyString) return 0;
  return parseFloat(currencyString.replace(/[^0-9.-]+/g, '')) || 0;
}

export default formatCurrency;
